import React, { useState, useEffect, useRef } from 'react'
import {
	FileHandlerUtils,
	FileUploaderComponent,
	NAPIAvatar,
	Avatar,
	ProgressRing,
} from '@adsk/napi-shared'
import styled from 'styled-components'
import { Flex } from '@adsk/alloy-react-box'
import {
	OnboardingUserAppContextType,
	useOnboardingUserContext,
} from '../../../store'
import ReactCrop, { type PixelCrop, type Crop } from 'react-image-crop'
import 'react-image-crop/dist/ReactCrop.css'
import { centerAspectCrop } from '../helpers'
import { AlertBanner, type BannerRef } from '@adsk/napi-shared'
import _ from 'lodash'
import {
	resetState,
	setProfilePictureAction,
	setSubmitState,
	updateAvatarState,
	updateCroppedState,
} from '../../../store/actions'
import { FileInfoType } from '../../../types/ActionTypes'
import { MinusIcon } from '@adsk/alloy-react-icon'
import Theme from '@adsk/alloy-react-theme'
import { device } from '@adsk/napi-shared'
import { STATIC_IMAGES } from '../../../constants'
import Illustration from '@adsk/alloy-react-illustration'
import { getInitials } from '../../../utils/helpers'
import { InfoPopover } from '@adsk/alloy-react-tooltip'
import { useTranslation } from '../../../../i18n'

const ImageRestrictionsToolTipMessage = () => {
	const { t } = useTranslation()
	return (
		<>
			<p>
				<b>{t('IMAGE_FILES')}</b>
			</p>
			<p>{t('IMAGE_FILES_BODY')}</p>
		</>
	)
}

const ProgressRingDiv = styled.div`
	padding: 15%;
	display: flex;
	justify-content: center;
	flex: 1;
	height: 220px;
	width: 220px;
	border: 1px dotted black;
`
const MainDiv = styled.div`
	display: flex;
	height: 100%;
	text-align: center;
	justify-content: center;
	align-items: center;
`
const AvatarDiv = styled.div`
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: center;
`
const ProfilePhoto = styled.img`
	width: 100%;
	height: 100%;
`
const AvatarProfileDiv = styled.div`
	position: absolute;
	cursor: pointer;
	bottom: 5%;
	right: 10%;
	@media${device.mobileXL} {
		right: 10%;
	}
`
const StyledReactCrop = styled(ReactCrop)`
	max-height: 100%;
	max-width: 100%;
	width: auto;
	height: auto;
`
const MAX_FILE_SIZE = 5242880 // 5MB
const BodyComponent = (): JSX.Element => {
	const SUPPORTED_IMAGE_FORMATS =
		'image/jpeg,image/jpg,image/png,image/gif,image/bmp'
	const { t } = useTranslation()
	const {
		state: { userInfo, fileInfo, errorInfo, showLoader },
		dispatch,
	} = useOnboardingUserContext() as OnboardingUserAppContextType
	const { colors, typography } = Theme
	const [crop, setCrop] = useState<Crop>()
	const fileInputRef = useRef<HTMLInputElement>(null)
	const imgRef = useRef<HTMLImageElement>(null)
	const previewCanvasRef = useRef<HTMLCanvasElement>(null)
	const bannerRef = useRef<BannerRef>(null)
	const [imgSrc, setImgSrc] = useState<string>('')
	const [isEmptyAvatarUrl, setIsEmptyAvatarUrl] = useState<boolean>(
		_.isEmpty(userInfo?.updatedAvatarUrl)
	)

	useEffect(() => {
		dispatch(resetState())
	}, [dispatch])

	useEffect(() => {
		// This effect is to reset the image preview when user clicks on remove image
		if (_.isEmpty(fileInfo)) {
			setImgSrc('')
		}
	}, [fileInfo])

	const onCompleteCropHandler = async (e: PixelCrop) => {
		await FileHandlerUtils.setCropCanvas(
			imgRef.current as HTMLImageElement,
			previewCanvasRef.current as HTMLCanvasElement,
			e,
			fileInfo?.file?.type
		)
		dispatch(
			updateCroppedState({
				imageRefElement: imgRef.current as HTMLImageElement,
				cropProps: {
					canvasRef: previewCanvasRef.current as HTMLCanvasElement,
					pixelCrop: e,
				},
			})
		)
	}
	const isFileValid = (file: File) => {
		const imageFormats = SUPPORTED_IMAGE_FORMATS.split(',')
		if (!file) {
			return false
		}
		return !(file?.size > MAX_FILE_SIZE || !imageFormats.includes(file?.type))
	}
	const getFilesFromEventHandler = async (files: File[]) => {
		const file = files?.[0] as File
		if (!isFileValid(file)) {
			bannerRef.current?.showBanner(t('CLIENT_ERROR'))
			return
		}
		bannerRef.current?.hideBanner()
		// Makes crop preview update between images.
		setCrop(undefined)
		const imgSrc = await FileHandlerUtils.getFileContent(file)
		dispatch(
			updateAvatarState({
				file,
			} as FileInfoType)
		)
		dispatch(setSubmitState(true))
		dispatch(setProfilePictureAction('upload'))
		setImgSrc(imgSrc)
		return []
	}

	const AvatarActionComponent = (): JSX.Element => {
		return (
			<AvatarProfileDiv
				role={'button'}
				onClick={(e) => {
					e.stopPropagation()
					setIsEmptyAvatarUrl(true)
					setImgSrc('')
					dispatch(setProfilePictureAction('remove'))
					dispatch(setSubmitState(true))
				}}
			>
				<MinusIcon
					data-testid={'profileRemoveIconTestId'}
					color={colors.white}
					style={{
						backgroundColor: colors.adskBlue500,
					}}
				/>
			</AvatarProfileDiv>
		)
	}

	const AvatarView = ({
		hideAvatar,
	}: {
		hideAvatar?: boolean
	}): JSX.Element => {
		return (
			<AvatarDiv data-testid={'customAvatarDivTestId'}>
				{/* Added flag to re-use the same Avatar View for multiple purpose */}
				{!hideAvatar && (
					<div style={{ position: 'relative', height: '100%' }}>
						<NAPIAvatar
							style={{
								alignSelf: 'center',
								height: '180px',
								width: '180px',
								...(imgSrc !== '' && { display: 'none' }),
							}}
							showPlaceHolder={true}
							fallbackImage={STATIC_IMAGES.PLACEHOLDER}
							placeHolder={{
								type: 'ProgressRing',
								size: 'small',
								styles: {
									alignSelf: 'center',
								},
							}}
							name={getInitials(`${userInfo?.firstName} ${userInfo?.lastName}`)}
							size={Avatar.SIZES.XXLARGE_128}
							image={userInfo?.updatedAvatarUrl}
							actionComponent={<AvatarActionComponent />}
						/>
					</div>
				)}
				<ImageRestrictionComponent />
			</AvatarDiv>
		)
	}

	const ImageRestrictionComponent = (): JSX.Element => {
		return (
			<>
				<input
					type={'file'}
					ref={fileInputRef}
					disabled={showLoader}
					accept={SUPPORTED_IMAGE_FORMATS}
					style={{ display: 'none' }}
					multiple={false}
					onClick={(e) => e.stopPropagation()}
					onChange={async (e) => {
						e.stopPropagation()
						const files = e.target.files
						if (files) {
							await getFilesFromEventHandler(Array.from(files))
						}
					}}
				/>
				<Flex flex={1} flexDirection={'column'}>
					<div
						style={{
							...typography.caption,
							cursor: 'pointer',
							fontStyle: 'normal',
							paddingTop: '10px',
							paddingBottom: '20px',
							color: colors.adskBlue500,
							textDecoration: 'none',
						}}
					>
						<div
							style={{ display: 'flex', justifyContent: 'center' }}
							role={'button'}
							onClick={(e) => {
								e.preventDefault()
								e.stopPropagation()
								if (fileInputRef.current) fileInputRef.current?.click()
							}}
						>
							<span>{t('PHOTO_CHOOSE')}</span>
							<InfoPopover
								message={<ImageRestrictionsToolTipMessage />}
								style={{
									paddingLeft: '2px',
								}}
								placement={InfoPopover.PLACEMENTS.RIGHT}
								variant={InfoPopover.VARIANTS.DARK}
								size={15}
							/>
						</div>
						<span style={{ color: colors.charcoal900 }} role={'presentation'}>
							{t('ACCEPTED_FORMATS')}
							<br />
							{t('MAXIMUM_FILESIZE')}
						</span>
					</div>
				</Flex>
			</>
		)
	}

	const renderCustomComponent = (): JSX.Element | null => {
		if (imgSrc === '' && !isEmptyAvatarUrl) {
			return <AvatarView />
		} else if (imgSrc !== '') {
			return <AvatarView hideAvatar />
		} else {
			return (
				<div style={{ margin: 'auto 25px' }}>
					<Illustration type={Illustration.TYPES.PEOPLE_HEADS_GREY} />
					<p style={{ color: colors.charcoal900 }}>{t('DRAG_AND_DROP')}</p>
				</div>
			)
		}
	}

	React.useEffect(() => {
		if (errorInfo?.message !== undefined) {
			bannerRef?.current?.showBanner(errorInfo?.message)
		} else {
			bannerRef?.current?.hideBanner()
		}
	}, [errorInfo])
	return (
		<>
			<AlertBanner
				ref={bannerRef}
				style={{
					position: 'relative',
					width: '100%',
					marginBottom: '2%',
				}}
			/>
			<MainDiv>
				{showLoader && (
					<div>
						<ProgressRingDiv>
							<ProgressRing
								message={
									<span
										style={{ fontWeight: 'light', color: colors.charcoal900 }}
									>
										{t('UPLOADING')}
									</span>
								}
							/>
						</ProgressRingDiv>
						<ImageRestrictionComponent />
					</div>
				)}
				{!showLoader && (
					<Flex
						flexDirection={'column'}
						alignItems={'center'}
						justifyContent={'center'}
					>
						<Flex flex={1}>
							{!!imgSrc && (
								<StyledReactCrop
									circularCrop
									ruleOfThirds
									aspect={1}
									crop={crop}
									keepSelection
									onChange={(_, percentCrop) => setCrop(percentCrop)}
									onComplete={async (e) => {
										if (imgRef.current) {
											await onCompleteCropHandler(e)
										}
									}}
								>
									<ProfilePhoto
										src={imgSrc}
										ref={imgRef}
										onLoad={(e) => {
											const { width, height } = e.currentTarget
											setCrop(centerAspectCrop(width, height, 1))
										}}
									/>
								</StyledReactCrop>
							)}
							<canvas
								ref={previewCanvasRef}
								style={{
									display: 'none',
									borderRadius: '50%',
									width: crop?.width,
									height: crop?.height,
								}}
							/>
						</Flex>
						<Flex flex={1} flexDirection={'column'}>
							<FileUploaderComponent
								renderDragActiveContent={() => <> {t('DROP_FILE')} </>}
								style={{
									...((imgSrc || !isEmptyAvatarUrl) && {
										border: 'none',
										padding: '0 !important',
									}),
									height: 'auto',
								}}
								accept={SUPPORTED_IMAGE_FORMATS}
								linkText={'Choose image file'}
								containerSize={'small'}
								customComponent={renderCustomComponent()}
								onDrop={async (acceptedFiles: File[]) => {
									await getFilesFromEventHandler(acceptedFiles)
								}}
							/>
							{isEmptyAvatarUrl && !imgSrc && <ImageRestrictionComponent />}
						</Flex>
					</Flex>
				)}
			</MainDiv>
		</>
	)
}

export default BodyComponent
